import React from 'react';

import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { map, reduce, uniq } from 'lodash/fp';
import { Link } from 'react-router-dom';

import AutocompleteSearch from '../components/AutocompleteSearch';
import Email from '../components/Email';
import axios from '../modules/axios';
import PromoteurInfoCell, { DetailsTooltip } from '../pages/centrale/PromoteurInfoCell';

export default {
  title: 'Centrale',
  name: 'centrale',
  icon: faEnvelope,
  resource: 'opti9_demande',
  primaryKey: 'iddemande',
  query: {
    _associations: 'status,lot,prog,entite,commercial',
    'prog._associations': 'promoteur,responsableGestionFinanciere,chargeReceptionsLots',
    'commercial._associations': 'partenaire',
    'commercial.partenaire._associations': 'commercialisateur',
    'status._fields': 'status',
    'prog._fields': 'ville,cp,adresse,dept,id,nom,contact_reservation_type,contact_denonce_type',
    'prog.promoteur._fields': 'responsable_vente,site_internet,login_site_internet,mdp_site_internet,idpromoteur,nom',
    'prog.chargeReceptionsLots._fields': 'email_pro',
    'prog.responsableGestionFinanciere._fields': 'email_pro',
  },
  sort: '-iddemande',
  head: {
    status: 'Statut',
    type: 'Type',
    date: 'Reçu le',
    idindividu: 'Commercial',
    client: 'Client',
    'prog.idprog': 'Programe',
    promoteur: 'Promoteur',
    '-responseattente': 'Réponse attente',
    '-responsedefinite': 'Réponse définitive',
    obs: 'Observations',
    memo: 'Memo',
  },
  /*
   * Transform raw rows before displaying them.
   */
  transformRows: async rows => {
    const idsentite = uniq(map(({ identite }) => identite, rows));

    try {
      const res = await axios.get(`entite_cliente?identite=${idsentite.join(',')}&_associations=client`);
      const clientByIdEntite = reduce(
        (acc, { identite, client }) => {
          return {
            ...acc,
            [identite]: client,
          };
        },
        {},
        res.data,
      );

      return map(
        centrale => ({
          ...centrale,
          clientObj: clientByIdEntite[centrale.identite],
        }),
        rows,
      );
    } catch (error) {
      return rows;
    }
  },
  list: ({ values }) => [
    values.status?.status ? (
      <Typography style={{ color: '#00e676' }}>En cours</Typography>
    ) : (
      <Typography style={{ color: '#ff5722' }}>À faire</Typography>
    ),
    values.type,
    values.date,
    () => {
      const email = values.commercial?.email || values.commercial?.email2 || values.commercial?.email_pro;
      const phone = values.commercial?.tel_dom || values.commercial?.tel_pro || values.commercial?.tel_port;

      return (
        <DetailsTooltip
          title={
            <>
              {values.commercial?.partenaire?.commercialisateur?.nom}
              <br />
              {phone}
              <br />
              <a href={`mailto:${email}`}>{email}</a>
            </>
          }
          interactive
        >
          <Link to={{ pathname: `/i9/individus/${values.commercial?.idindividu}` }}>
            {values.commercial?.nom} {values.commercial?.prenom}
          </Link>
        </DetailsTooltip>
      );
    },
    () => {
      const email = values.clientObj?.email || values.clientObj?.email_pro;

      return (
        <DetailsTooltip
          title={
            <>
              {`${values.clientObj?.ville} - ${values.clientObj?.cp}`}
              <br />
              <a href={`mailto:${email}`}>{email}</a>
              <br />
              {values.clientObj?.tel_port || values.clientObj?.tel_pro || values.clientObj?.tel_dom}
            </>
          }
          interactive
        >
          <Link to={{ pathname: `/i9/entites/${values.identite}` }}>{values.client}</Link>
        </DetailsTooltip>
      );
    },
    () => (
      <DetailsTooltip title={<>{`${values.prog?.ville} - ${values.prog?.dept}`}</>} interactive>
        <Link to={{ pathname: `/i9/programmes/${values.prog?.id}` }}>{values.prog?.nom}</Link>
      </DetailsTooltip>
    ),
    () => <PromoteurInfoCell values={values} />,
    <Email
      to={values.commercial?.email}
      subject={`[Immobilier Neuf] Demande de dénonce sur le programme ${values.prog?.nom}`}
      text={`Bonjour ${values.commercial?.prenom} ${values.commercial?.nom}\n\nNous avons bien pris en compte votre demande de dénonce sur le programme ${values.prog?.nom} situé à ${values.prog?.ville} pour le client ${values.client}.\n\nSans retour de notre part sous 72h, vous pourrez considérer cette dénonce comme acceptée.\n\nBien cordialement`}
    >
      <Button variant='contained' color='primary'>
        Attente
      </Button>
    </Email>,
    <Grid container direction='column' justifyContent='center' alignItems='center' spacing={1}>
      <Grid item xs>
        <Email
          to={values.commercial?.email}
          subject={` [Immobilier Neuf] Réponse à votre demande de dénonce sur le programme ${values.prog?.nom}`}
          text={`Bonjour ${values.commercial?.prenom} ${values.commercial?.nom}\n\nLa dénonciation pour le client ${values.client}, sur le prgramme ${values.prog?.nom} situé à ${values.prog?.ville} est validée.\n\nBien cordialement`}
        >
          <Button variant='contained' color='primary'>
            OUI
          </Button>
        </Email>
      </Grid>
      <Grid item xs>
        <Email
          to={values.commercial?.email}
          subject={` [Immobilier Neuf] Réponse à votre demande de dénonce sur le programme ${values.prog?.nom}`}
          text={`Bonjour ${values.commercial?.prenom} ${values.commercial?.nom}\n\nMalheureusement, la dénonciation pour le client ${values.client}, sur le programme ${values.prog?.nom} situé à ${values.prog?.ville} a été refusée.\n\nBien cordialement`}
        >
          <Button variant='contained' color='secondary'>
            NON
          </Button>
        </Email>
      </Grid>
    </Grid>,
    values.obs,
    values.memo,
  ],
  filter: ({ props, setFieldValue, handleSubmit }) => [
    <AutocompleteSearch
      {...props}
      resource='individu'
      optionLabel={option => option.nom + ' ' + option.prenom}
      attribute='nom'
      filters={{ conseiller: 1 }}
      label='Commercial'
      onChange={(event, value) => {
        setFieldValue('iddemandeur', value?.idindividu);
        handleSubmit();
      }}
    />,
  ],
  edit: ({ values, props }) => [
    <TextField {...props} fullWidth name='obs' label='Observation' value={values.obs} multiline minRows='3' />,
    <TextField {...props} fullWidth name='memo' label='Mémo' value={values.memo} multiline minRows='3' />,
  ],
  popup: true,
};
